<template>
  <main class="main main--has-floating-buttons">
    <portal to="headerTitle">
      {{ $t('admin.interfaces.pageLockingSettings.title') }}
    </portal>
    <template v-if="config !== null">
      <card :title="$t('admin.interfaces.pageChannelManagerCx.card.general')" cols="12 6@desktop" rows="1">
        <hm-card-inner>
          <hm-input-select v-model="config.defaultConfigurationId" :label="$t('admin.interfaces.pageLockingSettings.defaultLockingSystem')">
            <option :value="null"></option>
            <option v-for="it in config.keyManagementConfigurations" :key="it.id" :value="it.id">{{ it.name }}</option>
          </hm-input-select>
        </hm-card-inner>
        <hm-card-inner v-if="hasRole('ROLE_SYSADMIN')" title="Usage/Billing Details">
          <hm-col cols="12">
            <span class="info-text col-12">The following numbers are <b><u>not</u></b> realtime. They will be refreshed at least once a day.<br/>They are only visible to sysadmin users for now. They are not visible to the end-user for now.</span>
          </hm-col>
          <hm-input-number :value="hotelSettings.details.additionalFields.SEAM_LOCK_COUNT" cols="12" label="Managed Lock Count: Seam" disabled />
          <hm-input-number :value="hotelSettings.details.additionalFields.LOXONE_LOCK_COUNT" cols="12" label="Managed Lock Count: Loxone" disabled />
          <hm-input-number :value="hotelSettings.details.additionalFields.CHARPMS_LOCK_COUNT" cols="12" label="Managed Lock Count: Char" disabled />
        </hm-card-inner>
      </card>
      <template v-for="(it, idx) in config.keyManagementConfigurations">
        <card :title="`${it.name}`"
              :key="it.id"
              :cols="`12 6@desktop ${idx === 0 ? 'start-1' : ''}`"
              card-type="record"
              rows="3"
              @delete="deleteConfig(it.id)"
        >
          <hm-card-inner>
            <hm-input :value="it.id" :label="$t('admin.interfaces.pageLockingSettings.id')" disabled />
            <hm-input v-model="it.name" :label="$t('admin.interfaces.pageLockingSettings.name')" />
            <hm-input-toggle v-model="it.enabled"
                             :label="$t('admin.interfaces.pageLockingSettings.enabled')"
                             :enabled-title="$t('enabled')"
                             :disabled-title="$t('disabled')" />
            <hm-input-select v-model="it.lockingSystem" :label="$t('admin.interfaces.pageLockingSettings.lockingSystem')" :disabled="!hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')">
              <option v-if="hasLicense('InterfaceLoxone')" value="LoxoneNfcCodeTouch">{{ $t('LockingSystem.LoxoneNfcCodeTouch') }}</option>
              <option v-if="hasLicense('LockingSystems')" value="GenericKeySafe">{{ $t('LockingSystem.GenericKeySafe') }}</option>
              <option v-if="hasLicense('InterfaceSeam')" value="SeamLocks">{{ $t('LockingSystem.SeamLocks', [env('WHITE_LABEL_NAME')]) }}</option>
              <option v-if="hasLicense('InterfaceCharPmsLink') && hasLicense('InterfaceKeyCard')" value="CharPmsLink">{{ $t('LockingSystem.CharPmsLink', [env('WHITE_LABEL_NAME')]) }}</option>
            </hm-input-select>
            <hm-input-select v-model="it.keyType" :label="$t('admin.interfaces.pageLockingSettings.keyType')">
              <option v-for="it in enums.ADDITIONAL_DATA.LockingSystem[it.lockingSystem].keyTypes" :value="it" :key="it">{{ it }}</option>
            </hm-input-select>
            <template v-if="['CharPmsLink'].includes(it.lockingSystem)">
              <hm-input-select v-model="it.cfgCharPmsLink.configId" :label="$t('admin.interfaces.pageLockingSettings.charPmsLinkConfigId')" no-select>
                <option v-for="it in charPmsLinkConfigs" :value="it.id" :key="it" v-html="it.id"></option>
              </hm-input-select>
            </template>
            <template v-if="!['KeyCard'].includes(it.keyType)">
              <hm-input-number v-model="it.codeDigits" :label="$t('admin.interfaces.pageLockingSettings.codeDigits')" />
              <!-- multi lang guest description -->
              <hm-input v-if="['LoxoneNfcCodeTouch'].includes(it.lockingSystem)" v-model="it.username" :label="$t('admin.interfaces.pageLockingSettings.username')"/>
              <hm-input v-if="['LoxoneNfcCodeTouch'].includes(it.lockingSystem)" v-model="it.password" :label="$t('admin.interfaces.pageLockingSettings.password')" type="password" />
              <hm-input v-if="['SeamLocks'].includes(it.lockingSystem)"
                        v-model="it.apiKey"
                        :label="$t('admin.interfaces.pageLockingSettings.apiKey')"
                        :disabled="!hasRole('ROLE_SYSADMIN')"
                        type="password"/>
              <hm-input v-if="false" v-model="it.url" :label="$t('admin.interfaces.pageLockingSettings.url')" />
              <hm-input-select v-model="it.issueKeyEmailTemplateId" :label="$t('admin.interfaces.pageLockingSettings.issueKeyEmailTemplate')" :no-select="true">
                <option v-for="template in templates" :key="template.id" :value="template.id">{{ template.name }}</option>
              </hm-input-select>
              <hm-input-select v-model="it.revokeKeyEmailTemplateId" :label="$t('admin.interfaces.pageLockingSettings.revokeKeyEmailTemplate')" :no-select="true">
                <option v-for="template in templates" :key="template.id" :value="template.id">{{ template.name }}</option>
              </hm-input-select>
            </template>
            <template v-if="!['CharPmsLink'].includes(it.lockingSystem)">
              <hm-input-toggle v-model="it.autoCreateKeyOnCheckIn" :label="$t('admin.interfaces.pageLockingSettings.autoCreateKeyOnCheckIn')" />
              <hm-input-toggle v-model="it.autoRevokeAllKeysOnCheckOut" :label="$t('admin.interfaces.pageLockingSettings.autoRevokeAllKeysOnCheckOut')" />
            </template>
            <template v-if="['SeamLocks'].includes(it.lockingSystem)">
              <hm-input-toggle v-model="it.cfgSeam.disableConnectionEmails" :label="$t('admin.interfaces.pageLockingSettings.seamDisableConnectionEmails')" />
              <hm-input-toggle v-model="it.cfgSeam.disableBatteryEmails" :label="$t('admin.interfaces.pageLockingSettings.seamDisableBatteryEmails')" />
              <hm-multiselect v-model="it.ignoredEvents"
                              v-if="!isEmpty(it.ignoredEvents) || hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')"
                              :disabled="!hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')"
                              :options="['device.disconnected', 'device.connected', 'device.connection_became_flaky', 'device.connection_stabilized', 'device.low_battery'].map(it => ({ id: it, name: it }))"
                              :label="$t('admin.interfaces.pageLockingSettings.seamIgnoredEvents')"
                              :help="$t('admin.interfaces.pageLockingSettings.seamIgnoredEvents_help')"
                              name="ignoredEvents"
                              label-expression="${option.name}" />
            </template>
          </hm-card-inner>
          <hm-card-inner v-if="['SeamLocks'].includes(it.lockingSystem)" :title="$t('admin.interfaces.pageLockingSettings.card.seamLocks', [env('WHITE_LABEL_NAME')])">
            <hm-button icon-left="eye"
                       class="btn--green"
                       cols="12@mobile 6@tablet 4@desktop"
                       @click="$router.push({ name: 'interfaces.locking.devices', params: { interfaceId: it.id }})">{{ $t('admin.interfaces.pageLockingSettings.showSeamDevices') }}</hm-button>
            <hm-button icon-left="refresh"
                       class="btn--orange"
                       cols="12@mobile 6@tablet 4@desktop"
                       @click="updateControls(it.id)">{{ $t('admin.interfaces.pageLockingSettings.actionRetrieveControls') }}</hm-button>
            <hm-button icon-left="plus"
                       class="btn--blue"
                       cols="12@mobile 6@tablet 4@desktop"
                       @click="seamConnect(it.id)">{{ $t('admin.interfaces.pageLockingSettings.connectSeam', [env('WHITE_LABEL_NAME')]) }}</hm-button>
          </hm-card-inner>
          <hm-card-inner v-if="['LoxoneNfcCodeTouch'].includes(it.lockingSystem)" :title="$t('admin.interfaces.pageLockingSettings.card.loxone')">
            <hm-col cols="12">
              <span class="info-text col-12">{{ $t('admin.interfaces.pageLockingSettings.loxoneDnsInformation') }}</span>
            </hm-col>
            <hm-input v-model="it.cfgLoxone.miniserverSnr" :label="$t('admin.interfaces.pageLockingSettings.loxMiniserverSnr')" />
            <hm-input v-model="it.cfgLoxone.uiPassword" :label="$t('admin.interfaces.pageLockingSettings.loxUiPassword')" type="password" />
            <hm-input v-if="isEmpty(controlMap[it.id])" :value="it.cfgLoxone.controlId" :label="$t('admin.interfaces.pageLockingSettings.loxNfcCodeTouchControlId')" disabled />
            <hm-input-select v-else v-model="it.cfgLoxone.controlId" :label="$t('admin.interfaces.pageLockingSettings.loxNfcCodeTouchControlId')" no-select>
              <option v-for="it in controlMap[it.id]" :value="it.uuidAction" :key="it.uuidAction">{{ it.name }} - {{ it.room }} ({{ it.uuidAction }})</option>
            </hm-input-select>
            <hm-input-select v-model="it.cfgLoxone.defaultOutput" :label="$t('admin.interfaces.pageLockingSettings.loxDefaultOutput')">
              <option :value="null"></option>
              <option :value="1">q1 {{ getAdditionalOutputLabel(it.id, 'q1') }}</option>
              <option :value="2">q2 {{ getAdditionalOutputLabel(it.id, 'q2') }}</option>
              <option :value="3">q3 {{ getAdditionalOutputLabel(it.id, 'q3') }}</option>
              <option :value="4">q4 {{ getAdditionalOutputLabel(it.id, 'q4') }}</option>
              <option :value="5">q5 {{ getAdditionalOutputLabel(it.id, 'q5') }}</option>
              <option :value="6">q6 {{ getAdditionalOutputLabel(it.id, 'q6') }}</option>
            </hm-input-select>
            <hm-multiselect v-model="it.cfgLoxone.additionalOutputs"
                            :options="loxoneOutputs(it.id)"
                            :label="$t('admin.interfaces.pageLockingSettings.loxAdditionalOutputs')"
                            name="additionalOutputs"
                            label-expression="${option.name}" />
            <hm-button icon-left="refresh"
                       class="btn--orange"
                       cols="12@mobile 6@tablet 4@desktop"
                       @click="updateControls(it.id)">{{ $t('admin.interfaces.pageLockingSettings.actionRetrieveControls') }}</hm-button>
          </hm-card-inner>
        </card>
      </template>

      <card :title="$t('admin.interfaces.pageLockingSettings.card.rooms')" cols="12">
        <hm-card-inner :title="$t('admin.interfaces.pageLockingSettings.card.changeAll')">
          <hm-button cols="12 6@tablet 3@desktop 2@hd" class="btn--green" icon-left="save" @click="enableAllRooms">{{ $t('admin.interfaces.pageLockingSettings.actionEnableAllRooms') }}</hm-button>
          <hm-button cols="12 6@tablet 3@desktop 2@hd" class="btn--red" icon-left="close" @click="disableAllRooms">{{ $t('admin.interfaces.pageLockingSettings.actionDisableAllRooms') }}</hm-button>
          <hm-button cols="12 6@tablet 3@desktop 2@hd" class="btn--orange" icon-left="download" @click="selectDefaultAllRooms">{{ $t('admin.interfaces.pageLockingSettings.actionDefaultLockingSystemAllRooms') }}</hm-button>
        </hm-card-inner>
        <template v-for="roomConfig in config.roomConfigurations">
          <hm-card-inner :title="`${roomConfig.roomNumber}`" :key="roomConfig.roomNumber" :deletable="roomConfig.additionalFields.VIRTUAL_ROOM === 'true'" @delete="deleteVirtualRoom(roomConfig.id)">
            <hm-input-toggle v-model="roomConfig.enabled"
                             cols="12 6@tablet 3@desktop 2@hd"
                             label="Aktiviert?" />
            <hm-input-select v-model="roomConfig.keyManagementConfigurationId" :label="$t('admin.interfaces.pageLockingSettings.room.lockingSystem')" cols="12 6@tablet 3@desktop 2@hd">
              <option :value="null"></option>
              <option v-for="it in config.keyManagementConfigurations" :key="it.id" :value="it.id">{{ it.name }}</option>
            </hm-input-select>
            <hm-input v-if="roomConfig.additionalFields.VIRTUAL_ROOM === 'true'" v-model="roomConfig.additionalFields.VIRTUAL_ROOM_LABEL" :label="$t('admin.interfaces.pageLockingSettings.room.roomNumber')" cols="12 6@tablet 3@desktop 2@hd" />
            <!-- **
                 ** CHAR PMSLINK ***********
                 ** -->
            <template v-if="!isEmpty(configMap[roomConfig.keyManagementConfigurationId]) && configMap[roomConfig.keyManagementConfigurationId].lockingSystem === 'CharPmsLink'">
              <template v-if="!isEmpty(configMap[roomConfig.keyManagementConfigurationId].cfgCharPmsLink.configId)">
                <!--                <pre v-html="charPmsLinkConfigMap" />-->
                <template v-if="!isEmpty(charPmsLinkConfigMap[configMap[roomConfig.keyManagementConfigurationId].cfgCharPmsLink.configId])">
                  <template v-if="!isEmpty(charPmsLinkConfigMap[configMap[roomConfig.keyManagementConfigurationId].cfgCharPmsLink.configId].keyCardSettings)">
                    <hm-input-select v-model="roomConfig.externalRoomNumber"
                                     :label="$t('admin.interfaces.pageLockingSettings.charPmsLinkRoomNumber')"
                                     cols="12 6@tablet 3@desktop 2@hd"
                                     :no-select="!roomConfig.enabled">
                      <option v-for="it in charPmsLinkConfigMap[configMap[roomConfig.keyManagementConfigurationId].cfgCharPmsLink.configId].keyCardSettings.rooms"
                              :value="it"
                              :key="it"
                              v-html="it"/>
                    </hm-input-select>
                    <hm-multiselect v-model="roomConfig.cfgCharPmsLink.extraAccess"
                                    :options="charPmsLinkConfigMap[configMap[roomConfig.keyManagementConfigurationId].cfgCharPmsLink.configId].keyCardSettings.extraAccess.map(it => ({id: it, name: it}))"
                                    :label="$t('admin.interfaces.pageLockingSettings.charPmsLinkExtraAccess')"
                                    label-expression="${option.name}"
                                    cols="12 6@tablet 3@desktop 2@hd"
                                    no-select>
                    </hm-multiselect>
                  </template>
                </template>
              </template>
            </template>
            <!-- **
                 ** SEAM ***********
                 ** -->
            <template v-if="!isEmpty(configMap[roomConfig.keyManagementConfigurationId]) && configMap[roomConfig.keyManagementConfigurationId].lockingSystem === 'SeamLocks'">
              <hm-input v-if="isEmpty(controlMap) || isEmpty(controlMap[roomConfig.keyManagementConfigurationId])"
                        :value="roomConfig.cfgSeam.deviceId"
                        :label="$t('admin.interfaces.pageLockingSettings.seamDeviceId')"
                        cols="12 6@tablet 3@desktop 2@hd"
                        disabled />
              <hm-input-select v-else v-model="roomConfig.cfgSeam.deviceId" :label="$t('admin.interfaces.pageLockingSettings.seamDeviceId')" cols="12 6@tablet 3@desktop 2@hd" :no-select="!roomConfig.enabled">
                <option v-for="it in controlMap[roomConfig.keyManagementConfigurationId]" :value="it.deviceId" :key="it.deviceId">{{ it.properties.name }} - {{ it.properties.additionalProperties.manufacturer }} ({{ it.deviceId }})</option>
              </hm-input-select>
              <hm-multiselect v-if="roomConfig.additionalFields.VIRTUAL_ROOM !== 'true'"
                              v-model="roomConfig.additionalRoomNumbers"
                              :disabled="!hasRole('ROLE_SYSADMIN')"
                              :options="config.roomConfigurations.map(it => ({ id: it.roomNumber, name: (isNil(it.additionalFields) || isNil(it.additionalFields.VIRTUAL_ROOM)) ? it.roomNumber : `${it.roomNumber} (${it.additionalFields.VIRTUAL_ROOM_LABEL})` }))"
                              :label="$t('admin.interfaces.pageLockingSettings.room.additionalRoomNumbers')"
                              :help="$t('admin.interfaces.pageLockingSettings.room.additionalRoomNumbers_help')"
                              name="additionalRoomNumbers"
                              label-expression="${option.name}"
                              cols="12 6@tablet 3@desktop 2@hd" />
              <hm-input-toggle v-if="roomConfig.additionalFields.VIRTUAL_ROOM === 'true'" v-model="roomConfig.doNotSendEmail" :label="$t('admin.interfaces.pageLockingSettings.room.doNotSendEmail')" cols="12 6@tablet 3@desktop 2@hd" />
              <template v-if="hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN') && !isNil(roomConfig.cfgSeam)">
                <hm-input-number v-model="roomConfig.cfgSeam.codeDigitsOverride" :label="$t('admin.interfaces.pageLockingSettings.seamRoomCodeDigitsOverride')" :required="false" cols="12 6@tablet 3@desktop 2@hd" />
                <hm-input-toggle :value="roomConfig.cfgSeam.lockingSystemGeneratedCode" :label="$t('admin.interfaces.pageLockingSettings.seamRoomLockingSystemGeneratedCode')" disabled cols="12 6@tablet 3@desktop 2@hd" />
              </template>
            </template>
            <!-- ***********************
                 ** LOXONE *************
                 *********************** -->
            <template v-if="!isEmpty(configMap[roomConfig.keyManagementConfigurationId]) && configMap[roomConfig.keyManagementConfigurationId].lockingSystem === 'LoxoneNfcCodeTouch'">
              <hm-input v-if="isEmpty(controlMap[roomConfig.keyManagementConfigurationId])"
                        :value="roomConfig.cfgLoxone.controlId"
                        :label="$t('admin.interfaces.pageLockingSettings.loxNfcCodeTouchControlId')"
                        cols="12 6@tablet 3@desktop 2@hd"
                        disabled />
              <hm-input-select v-else
                               v-model="roomConfig.cfgLoxone.controlId"
                               :label="$t('admin.interfaces.pageLockingSettings.loxNfcCodeTouchControlId')"
                               cols="12 6@tablet 3@desktop 2@hd"
                               :no-select="!roomConfig.enabled">
                <option v-for="it in controlMap[roomConfig.keyManagementConfigurationId]" :value="it.uuidAction" :key="it.uuidAction">{{ it.name }} - {{ it.room }} ({{ it.uuidAction }})</option>
              </hm-input-select>
              <hm-input-select v-model="roomConfig.cfgLoxone.defaultOutput" :label="$t('admin.interfaces.pageLockingSettings.room.defaultOutput')" cols="12 6@tablet 3@desktop 2@hd">
                <option :value="null"></option>
                <option :value="1">q1 {{ getAdditionalOutputLabel(roomConfig.keyManagementConfigurationId, 'q1', roomConfig.cfgLoxone.controlId) }}</option>
                <option :value="2">q2 {{ getAdditionalOutputLabel(roomConfig.keyManagementConfigurationId, 'q2', roomConfig.cfgLoxone.controlId) }}</option>
                <option :value="3">q3 {{ getAdditionalOutputLabel(roomConfig.keyManagementConfigurationId, 'q3', roomConfig.cfgLoxone.controlId) }}</option>
                <option :value="4">q4 {{ getAdditionalOutputLabel(roomConfig.keyManagementConfigurationId, 'q4', roomConfig.cfgLoxone.controlId) }}</option>
                <option :value="5">q5 {{ getAdditionalOutputLabel(roomConfig.keyManagementConfigurationId, 'q5', roomConfig.cfgLoxone.controlId) }}</option>
                <option :value="6">q6 {{ getAdditionalOutputLabel(roomConfig.keyManagementConfigurationId, 'q6', roomConfig.cfgLoxone.controlId) }}</option>
              </hm-input-select>
              <hm-multiselect v-model="roomConfig.cfgLoxone.additionalOutputs"
                              :options="loxoneOutputs(roomConfig.keyManagementConfigurationId)"
                              :label="$t('admin.interfaces.pageLockingSettings.room.additionalOutputs')"
                              name="additionalOutputs"
                              label-expression="${option.name}"
                              cols="12 6@tablet 3@desktop 2@hd" />
              <!-- :options="hotelSettings.rooms.all(roomConfig.additionalRoomNumbers).filter(it => it.roomNumber !== roomConfig.roomNumber).map(it => ({ id: it.roomNumber, name: it.roomNumber }))" -->
              <hm-multiselect v-if="roomConfig.additionalFields.VIRTUAL_ROOM !== 'true'"
                              v-model="roomConfig.additionalRoomNumbers"
                              :disabled="!hasRole('ROLE_SYSADMIN')"
                              :options="config.roomConfigurations.map(it => ({ id: it.roomNumber, name: (isNil(it.additionalFields) || isNil(it.additionalFields.VIRTUAL_ROOM)) ? it.roomNumber : `${it.roomNumber} (${it.additionalFields.VIRTUAL_ROOM_LABEL})` }))"
                              :label="$t('admin.interfaces.pageLockingSettings.room.additionalRoomNumbers')"
                              :help="$t('admin.interfaces.pageLockingSettings.room.additionalRoomNumbers_help')"
                              name="additionalRoomNumbers"
                              label-expression="${option.name}"
                              cols="12 6@tablet 3@desktop 2@hd" />
              <hm-input-toggle v-if="roomConfig.additionalFields.VIRTUAL_ROOM === 'true'" v-model="roomConfig.doNotSendEmail" :label="$t('admin.interfaces.pageLockingSettings.room.doNotSendEmail')" cols="12 6@tablet 3@desktop 2@hd" />
            </template>
          </hm-card-inner>
        </template>
      </card>


      <!--      <template v-if="!isEmpty(config.keyManagementConfigurations)">-->
      <!--        <tabable-card cols="12 6@desktop start-1">-->
      <!--          <template v-for="lang in [hotelSettings.details.primaryLanguage, ...hotelSettings.details.additionalLanguages]">-->
      <!--            <tabable-card-content :title="$t(`lang.${lang}`)" :key="lang">-->
      <!--              <hm-card-inner full-size>-->
      <!--                <hm-col cols="12">-->
      <!--                  <span class="info-text info-text&#45;&#45;full-size info-text&#45;&#45;no-margin col-12" v-html="$t('admin.interfaces.pageLockingSettings.infoTextGuestDescription')"></span>-->
      <!--                </hm-col>-->
      <!--              </hm-card-inner>-->
      <!--              <template v-for="(config, idx) in config.keyManagementConfigurations">-->
      <!--                <hm-card-inner :title="config.name" :key="idx">-->
      <!--                  <hm-col cols="12">-->
      <!--                    <ckeditor :id="`editor_${lang}_${idx}`" v-model="config.guestDescription[lang]" :incard="false" height="150" />-->
      <!--                  </hm-col>-->
      <!--                </hm-card-inner>-->
      <!--              </template>-->
      <!--            </tabable-card-content>-->
      <!--          </template>-->
      <!--        </tabable-card>-->
      <!--      </template>-->
      <!--    </template>-->

      <hm-floating-buttons v-if="!isNil(config)">
        <hm-floating-button :label="$t('admin.interfaces.pageLockingSettings.actionAdd')" color="blue" icon="plus">
          <hm-floating-child-button v-if="hasRole('ROLE_SYSADMIN')"
                                    :label="$t('admin.interfaces.pageLockingSettings.actionAddVirtualRoom')"
                                    color="blue"
                                    icon="plus"
                                    @click="addVirtualRoom()"/>
          <hm-floating-child-button v-if="hasLicense('InterfaceCharPmsLink') && hasLicense('InterfaceKeyCard')"
                                    :label="$t('admin.interfaces.pageLockingSettings.actionAddCharPmsLink')"
                                    color="blue"
                                    icon="plus"
                                    @click="addLockingSystem('char pmslink', 'CharPmsLink', 'KeyCard')"/>
          <hm-floating-child-button v-if="hasLicense('InterfaceLoxone')"
                                    :label="$t('admin.interfaces.pageLockingSettings.actionAddLoxone')"
                                    color="blue"
                                    icon="plus"
                                    @click="addLockingSystem('Loxone', 'LoxoneNfcCodeTouch', 'AutoGeneratedCode')"/>
          <hm-floating-child-button v-if="hasLicense('InterfaceSeam')"
                                    :label="$t('admin.interfaces.pageLockingSettings.actionAddSeam', [env('WHITE_LABEL_NAME')])"
                                    color="blue"
                                    icon="plus"
                                    @click="addLockingSystem($t('LockingSystem.SeamLocks', ['Hotelsoftware']), 'SeamLocks', 'AutoGeneratedCode')"/>
        </hm-floating-button>
      </hm-floating-buttons>
  </template></main>
</template>

<script>
/* eslint func-names: 0 */

import HotelmeisterMixin from '@/mixins/HotelmeisterMixin';
import { cloneDeep, find, findIndex, isEmpty, isNil, keyBy, max, sortBy } from 'lodash';
import SaveWatchMixin from '@/mixins/SaveWatchMixin';
import Card from '../../../lib/layout/Card';
import HmInputCheckbox from '../../../lib/inputs/HmInputCheckbox';
import HmInputMoney from '../../../lib/inputs/HmInputMoney';
import HmCardInner from '../../../lib/layout/HmCardInner';
import UpdateHotelSettingsMixin from '../../../../mixins/UpdateHotelSettingsMixin';
import HmInputSelect from '../../../lib/inputs/HmInputSelect';
import HmTextarea from '../../../lib/inputs/HmTextarea.vue';
import AiTextGenerator from '../../../lib/util/AiTextGenerator.vue';
import uuid from 'uuid';
import HmFloatingButton from '../../../lib/inputs/HmFloatingButton.vue';
import HmFloatingChildButton from '../../../lib/inputs/HmFloatingChildButton.vue';
import HmInputToggle from '../../../lib/inputs/HmInputToggle.vue';
import HmInputNumber from '../../../lib/inputs/HmInputNumber.vue';
import HmInput from '../../../lib/inputs/HmInput.vue';
import HmButton from '../../../lib/buttons/HmButton.vue';
import TabableCard from '../../../lib/layout/TabableCard.vue';
import HmCol from '../../../lib/layout/HmCol.vue';
import HmMultiselect from '../../../lib/inputs/HmMultiselect.vue';

export default {
  name: 'PageLockingSettings',
  components: { HmMultiselect, HmCol, TabableCard, HmButton, HmInput, HmInputNumber, HmInputToggle, HmFloatingChildButton, HmFloatingButton, AiTextGenerator, HmTextarea, HmInputSelect, HmCardInner, HmInputMoney, HmInputCheckbox, Card },
  mixins: [HotelmeisterMixin, SaveWatchMixin, UpdateHotelSettingsMixin],

  autosave: [
    {
      name: 'config',
      variable: 'config',
      event: 'hotelSettings.update',
      fn: function (cbSaveComplete) {
        const config = cloneDeep(this.config);

        this.axios.put(this.serviceUrls.interfaces.locking.get(this), config)
          .then((response) => {
            this.showMessages(response);
            cbSaveComplete();
          })
          .catch((error) => {
            if (isNil(error.response)) this.showErrorMessage();
            this.showMessages(error.response);
            cbSaveComplete();
          });
      },
    },
  ],

  data() {
    return {
      config: null,
      controlMap: { },
      templates: [],
      charPmsLinkConfigs: [],
    };
  },

  computed: {
    enums: function () {
      return this.$store.getters['enums/enums'];
    },
    roomMap: function () {
      return keyBy(this.hotelSettings.rooms, 'roomNumber');
    },
    configMap: function () {
      if (isNil(this.config)) return {};
      return keyBy(this.config.keyManagementConfigurations, 'id');
    },
    canEnable() {
      return true;
    },
    charPmsLinkConfigMap() {
      return keyBy(this.charPmsLinkConfigs, 'id');
    },
  },

  watch: {
  },

  created: function () {
    this.loadData();
  },

  updated() {
  },

  beforeDestroy() {
  },

  methods: {
    isEmpty,
    loadData() {
      this.axios.get(this.serviceUrls.interfaces.locking.get(this))
        .then((response) => {
          this.config = response.data.data;
        })
        .catch((error) => {
          if (isNil(error.response)) this.showErrorMessage();
          this.showMessages(error.response);
        });

      this.axios.get(this.serviceUrls.interfaces.charPmsLink.list(this))
        .then((response) => {
          this.charPmsLinkConfigs = response.data.data;
        });

      this.updateTemplates();
    },
    updateTemplates() {
      this.axios.get(this.serviceUrls.templateHelper.filter(this, 'Email'))
        .then((response) => {
          let filteredTemplates = response.data.data;

          filteredTemplates = filteredTemplates.filter(it => it.type === 'GenericEmail');
          filteredTemplates = sortBy(filteredTemplates, ['name']);

          this.templates = filteredTemplates;
        });
    },
    addLockingSystem(name = 'Generic', type = 'GenericKeySafe', key = 'PredefinedCode') {
      const cfg = {
        id: uuid.v4(),
        name: name,
        enabled: false,
        lockingSystem: type,
        keyType: key,
        codeDigits: 6,
        guestDescription: { },
        username: null,
        password: null,
        apiKey: null,
        url: null,
        issueKeyEmailTemplateId: null,
        revokeKeyEmailTemplateId: null,
        cfgLoxone: {
          miniserverSnr: null,
          uiPassword: null,
          controlId: null,
          defaultOutput: null,
          additionalOutputs: [],
        },
        cfgCharPmsLink: {
          configId: null,
        },
        cfgSeam: {
          disableConnectionEmails: false,
          disableBatteryEmails: false,
        },
        additionalFields: { },
      };

      if (type !== 'LoxoneNfcCodeTouch') cfg.cfgLoxone = null;

      this.config.keyManagementConfigurations.push(cfg);
    },
    updateControls(lockingSystemId) {
      const cfg = this.configMap[lockingSystemId];
      if (isNil(cfg)) {
        this.showErrorMessage();
        return;
      }

      if (isNil(this.controlMap[lockingSystemId])) this.$set(this.controlMap, lockingSystemId, []);

      switch (this.configMap[lockingSystemId].lockingSystem) {
        case 'LoxoneNfcCodeTouch':
          this.axios.get(this.serviceUrls.interfaces.locking.loxone.controls(this, lockingSystemId))
            .then((response) => {
              this.controlMap[lockingSystemId] = response.data.data;
              this.showSuccessMessage();
            })
            .catch((error) => {
              this.controlMap[lockingSystemId] = [];
              if (isNil(error.response)) this.showErrorMessage();
              this.showMessages(error.response);
            });
          break;
        case 'SeamLocks':
          this.axios.get(this.serviceUrls.interfaces.locking.seam.locks(this, lockingSystemId))
            .then((response) => {
              this.controlMap[lockingSystemId] = response.data.data;
              this.showSuccessMessage();
            })
            .catch((error) => {
              this.controlMap[lockingSystemId] = [];
              if (isNil(error.response)) this.showErrorMessage();
              this.showMessages(error.response);
            });
          break;
        default:
          // do nothing for now
          break;
      }
    },
    getAdditionalOutputLabel(lockingSystemId, outputId, pControlId) {
      const controlId = isEmpty(pControlId) ? this.configMap[lockingSystemId].cfgLoxone.controlId : pControlId;

      if (isEmpty(this.controlMap[lockingSystemId])) return '';
      if (isEmpty(controlId)) return '';

      const control = find(this.controlMap[lockingSystemId], it => it.uuidAction === controlId);

      if (isEmpty(control.details)) return '';
      if (isEmpty(control.details.accessOutputs)) return '';

      return control.details.accessOutputs[outputId] || '';
    },
    loxoneOutputs(lockingSystemId) {
      return [
        { id: 1, name: `q1 ${this.getAdditionalOutputLabel(lockingSystemId, 'q1')}` },
        { id: 2, name: `q2 ${this.getAdditionalOutputLabel(lockingSystemId, 'q2')}` },
        { id: 3, name: `q3 ${this.getAdditionalOutputLabel(lockingSystemId, 'q3')}` },
        { id: 4, name: `q4 ${this.getAdditionalOutputLabel(lockingSystemId, 'q4')}` },
        { id: 5, name: `q5 ${this.getAdditionalOutputLabel(lockingSystemId, 'q5')}` },
        { id: 6, name: `q6 ${this.getAdditionalOutputLabel(lockingSystemId, 'q6')}` },
      ];
    },
    disableAllRooms() {
      this.config.roomConfigurations.forEach((it) => { it.enabled = false; });
    },
    enableAllRooms() {
      this.config.roomConfigurations.forEach((it) => { it.enabled = true; });
    },
    selectDefaultAllRooms() {
      this.config.roomConfigurations.forEach((it) => { it.keyManagementConfigurationId = this.config.defaultConfigurationId; });
    },
    deleteConfig(configurationId) {
      const it = this.configMap[configurationId];
      const idx = this.config.keyManagementConfigurations.indexOf(it);
      if (idx >= 0) {
        this.config.keyManagementConfigurations.splice(idx, 1);
      }
    },
    env: function (name) {
      return process.env[name];
    },
    seamConnect(configurationId) {
      this.axios.get(this.serviceUrls.interfaces.locking.seam.connect(this, configurationId))
        .then((response) => {
          window.open(response.data.data.url, 'seam');
        })
        .catch((error) => {
          if (isNil(error.response)) this.showErrorMessage();
          this.showMessages(error.response);
        });
    },
    addVirtualRoom() {
      if (!isNil(this.config) && !isNil(this.config.roomConfigurations)) {
        const maxVirtualRoomNumber = max(this.config.roomConfigurations
          .filter(it => !isNil(it.additionalFields) && it.additionalFields.VIRTUAL_ROOM === 'true')
          .map(it => it.roomNumber.substring(3, 999))
          .map(it => parseInt(it, 10)));

        const newRoomNumber = isNil(maxVirtualRoomNumber) ? 1 : (maxVirtualRoomNumber + 1);

        this.config.roomConfigurations.push({
          id: uuid.v4(),
          enabled: false,
          code: null,
          roomNumber: `VR-${newRoomNumber}`,
          externalRoomNumber: null,
          additionalRoomNumbers: null,
          keyManagementConfigurationId: null,
          cfgLoxone: {},
          cfgSeam: {},
          additionalFields: {
            VIRTUAL_ROOM: 'true',
            VIRTUAL_ROOM_LABEL: `Virtual Room ${newRoomNumber}`,
          },
        });
      }
    },
    deleteVirtualRoom(virtualRoomId) {
      const idx = findIndex(this.config.roomConfigurations, it => it.id === virtualRoomId);
      if (idx >= 0) {
        this.config.roomConfigurations.splice(idx, 1);
      }
    },
  },
};
</script>

<!-- Scoped SCSS  -->
<style lang="scss" scoped>
</style>

<style lang="scss">

</style>



// WEBPACK FOOTER //
// src/components/pages/admin/interfaces/PageLockingSettings.vue